/**
 * AdvancedModePanel.js - Advanced Mode UI for Video Creation
 * 4-step accordion flow with full customization
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FiUpload,
  FiImage,
  FiX,
  FiCheck,
  FiChevronDown,
  FiRefreshCw,
  FiPlay,
  FiDownload,
  FiAlertCircle,
  FiInfo,
} from "react-icons/fi";
import { sanitizeError } from "../utils/errorSanitizer";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3010";

// Duration presets
const DURATION_PRESETS = [
  { id: "15s", label: "15s", desc: "Flash sale" },
  { id: "20s", label: "20s", desc: "Giới thiệu nhanh" },
  { id: "30s", label: "30s", desc: "Phổ biến", recommended: true },
  { id: "45s", label: "45s", desc: "Chi tiết" },
  { id: "60s", label: "60s", desc: "Đầy đủ" },
];

// Voice options grouped by gender (30 voices from Gemini TTS docs)
const VOICE_GROUPS = [
  {
    label: "👩 Giọng nữ",
    voices: [
      { id: "Zephyr", name: "Zephyr", category: "Tươi sáng" },
      { id: "Kore", name: "Kore", category: "Chắc chắn" },
      { id: "Aoede", name: "Aoede", category: "Nhẹ nhàng" },
      { id: "Leda", name: "Leda", category: "Trẻ trung" },
      { id: "Callirrhoe", name: "Callirrhoe", category: "Thoải mái" },
      { id: "Autonoe", name: "Autonoe", category: "Rạng rỡ" },
      { id: "Despina", name: "Despina", category: "Mượt mà" },
      { id: "Erinome", name: "Erinome", category: "Rõ ràng" },
      { id: "Laomedeia", name: "Laomedeia", category: "Vui tươi" },
      { id: "Achernar", name: "Achernar", category: "Dịu dàng" },
      { id: "Pulcherrima", name: "Pulcherrima", category: "Tự tin" },
      { id: "Vindemiatrix", name: "Vindemiatrix", category: "Êm dịu" },
      { id: "Sulafat", name: "Sulafat", category: "Ấm áp" },
    ],
  },
  {
    label: "👨 Giọng nam",
    voices: [
      { id: "Puck", name: "Puck", category: "Lạc quan" },
      { id: "Charon", name: "Charon", category: "Điềm đạm" },
      { id: "Fenrir", name: "Fenrir", category: "Sôi nổi" },
      { id: "Orus", name: "Orus", category: "Vững vàng" },
      { id: "Enceladus", name: "Enceladus", category: "Thì thầm" },
      { id: "Iapetus", name: "Iapetus", category: "Trong trẻo" },
      { id: "Umbriel", name: "Umbriel", category: "Thư thái" },
      { id: "Algieba", name: "Algieba", category: "Mượt mà" },
      { id: "Algenib", name: "Algenib", category: "Trầm khàn" },
      { id: "Rasalgethi", name: "Rasalgethi", category: "Chuyên nghiệp" },
      { id: "Alnilam", name: "Alnilam", category: "Quyết đoán" },
      { id: "Schedar", name: "Schedar", category: "Điềm tĩnh" },
      { id: "Gacrux", name: "Gacrux", category: "Chín chắn" },
      { id: "Achird", name: "Achird", category: "Thân thiện" },
      { id: "Zubenelgenubi", name: "Zubenelgenubi", category: "Tự nhiên" },
      { id: "Sadachbia", name: "Sadachbia", category: "Năng động" },
      { id: "Sadaltager", name: "Sadaltager", category: "Uyên bác" },
    ],
  },
];

// Speed options
const SPEED_OPTIONS = [
  { value: 0.8, label: "0.8x" },
  { value: 0.9, label: "0.9x" },
  { value: 1.0, label: "1.0x" },
  { value: 1.1, label: "1.1x" },
  { value: 1.2, label: "1.2x" },
  { value: 1.3, label: "1.3x" },
];

// Aspect ratios
const ASPECT_RATIOS = [
  { id: "16:9", label: "Ngang", icon: "🖥️", desc: "YouTube, Website" },
  { id: "9:16", label: "Dọc", icon: "📱", desc: "TikTok, Reels", recommended: true },
  { id: "1:1", label: "Vuông", icon: "⬛", desc: "Facebook Feed" },
];

// Visual effects
const VISUAL_EFFECTS = [
  { id: "zoom-in", label: "Phóng to", icon: "🔍" },
  { id: "zoom-out", label: "Thu nhỏ", icon: "🔎" },
  { id: "pan-left", label: "Trượt trái", icon: "⬅️" },
  { id: "pan-right", label: "Trượt phải", icon: "➡️" },
  { id: "static", label: "Tĩnh", icon: "⏸️" },
];

// Subtitle effects
const SUBTITLE_EFFECTS = [
  { id: "highlight", label: "Highlight" },
  { id: "bounce", label: "Nhảy" },
  { id: "typewriter", label: "Máy đánh chữ" },
  { id: "karaoke", label: "Karaoke" },
];

// Unique ID counter for stable drag keys
let imageIdCounter = 0;
const generateImageId = () => `img_${Date.now()}_${++imageIdCounter}`;

// Sortable image item for drag-and-drop reordering
const SortableImageItem = ({ id, src, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    transition: {
      duration: 200,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`image-preview-item ${isDragging ? "dragging" : ""}`}
    >
      {/* Drag handle overlay */}
      <div
        {...attributes}
        {...listeners}
        className="image-drag-handle"
      />
      <img src={src} alt={`Preview ${index + 1}`} />
      {/* Order badge */}
      <div className="image-order-badge">{index + 1}</div>
      <button
        className="image-preview-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
      >
        <FiX />
      </button>
    </div>
  );
};

const AdvancedModePanel = ({ initialImages = [], initialResult = null, onReset }) => {
  // State
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  // Step 1: Images
  const [imageItems, setImageItems] = useState(() =>
    initialImages.map((src) => ({ id: generateImageId(), src }))
  );
  // Derived plain array for API calls and export
  const images = imageItems.map((item) => item.src);
  const [duration, setDuration] = useState("30s");
  const [additionalInfo, setAdditionalInfo] = useState("");
  
  // Step 2: Script
  const [script, setScript] = useState(initialResult?.script || "");
  const [suggestedVoice, setSuggestedVoice] = useState(initialResult?.settings?.suggestedVoice || "Zephyr");
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  
  // Step 3: Voice
  const [selectedVoice, setSelectedVoice] = useState(suggestedVoice);
  const [speed, setSpeed] = useState(initialResult?.settings?.suggestedSpeed || 1.0);
  const [audioBase64, setAudioBase64] = useState(initialResult?.audioBase64 || null);
  const [audioMimeType, setAudioMimeType] = useState(initialResult?.audioMimeType || "audio/wav");
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  
  // Step 4: Export
  const [aspectRatio, setAspectRatio] = useState(initialResult?.settings?.suggestedAspectRatio || "9:16");
  const [effect, setEffect] = useState(initialResult?.settings?.suggestedEffect || "zoom-in");
  const [subtitleEnabled, setSubtitleEnabled] = useState(initialResult?.settings?.subtitleEnabled ?? true);
  const [subtitleEffect, setSubtitleEffect] = useState(initialResult?.settings?.subtitleEffect || "highlight");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [prevImageIndex, setPrevImageIndex] = useState(-1);
  const [imageTransitioning, setImageTransitioning] = useState(false);
  const [timePerImageState, setTimePerImageState] = useState(4);
  
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const userId = localStorage.getItem("userId");

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 8 },
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // Handle drag end - reorder images
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setImageItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  // Initialize from Quick Mode result
  useEffect(() => {
    if (initialResult) {
      setScript(initialResult.script || "");
      setSuggestedVoice(initialResult.settings?.suggestedVoice || "Zephyr");
      setSelectedVoice(initialResult.settings?.suggestedVoice || "Zephyr");
      setSpeed(initialResult.settings?.suggestedSpeed || 1.0);
      setAspectRatio(initialResult.settings?.suggestedAspectRatio || "9:16");
      setEffect(initialResult.settings?.suggestedEffect || "zoom-in");
      setSubtitleEnabled(initialResult.settings?.subtitleEnabled ?? true);
      setSubtitleEffect(initialResult.settings?.subtitleEffect || "highlight");
      setAudioBase64(initialResult.audioBase64 || null);
      setAudioMimeType(initialResult.audioMimeType || "audio/wav");
      
      // Mark steps as completed
      setCompletedSteps([1, 2, 3]);
      setActiveStep(4);
    }
  }, [initialResult]);

  // Handle file upload
  const handleFileSelect = useCallback((files) => {
    const validFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (validFiles.length + imageItems.length > 10) {
      setError("Tối đa 10 hình ảnh");
      return;
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageItems((prev) => [...prev, { id: generateImageId(), src: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });

    setError(null);
  }, [imageItems.length]);

  // Remove image by stable ID
  const removeImage = (id) => {
    setImageItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Generate script (Step 1 -> 2)
  const handleGenerateScript = async () => {
    if (images.length === 0) {
      setError("Vui lòng thêm ít nhất 1 hình ảnh");
      return;
    }

    setIsGeneratingScript(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/video/generate-script`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ images, targetDuration: duration, additionalInfo: additionalInfo.trim() || undefined }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Không thể tạo kịch bản");
      }

      setScript(data.script);
      setSuggestedVoice(data.suggestedVoice || "Zephyr");
      setSelectedVoice(data.suggestedVoice || "Zephyr");
      setCompletedSteps((prev) => [...prev.filter((s) => s !== 1), 1]);
      setActiveStep(2);
    } catch (err) {
      setError(sanitizeError(err));
    } finally {
      setIsGeneratingScript(false);
    }
  };

  // Generate audio (Step 2 -> 3)
  const handleGenerateAudio = async () => {
    if (!script.trim()) {
      setError("Vui lòng nhập kịch bản");
      return;
    }

    setIsGeneratingAudio(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/video/generate-speech`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: script,
          voiceName: selectedVoice,
          speed: speed,
          userId: userId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error("Không đủ điểm. Vui lòng nạp thêm điểm.");
        }
        throw new Error(data.error || "Không thể tạo giọng đọc");
      }

      setAudioBase64(data.audioBase64);
      setAudioMimeType(data.mimeType || "audio/wav");
      setCompletedSteps((prev) => [...prev.filter((s) => s !== 2 && s !== 3), 2, 3]);
      setActiveStep(4);
    } catch (err) {
      setError(sanitizeError(err));
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Calculate time per image based on audio duration
  const getTimePerImage = (duration, imageCount) => {
    if (imageCount <= 0) return duration;
    return duration / imageCount;
  };

  // Play preview audio
  const handlePlayAudio = () => {
    if (!audioBase64) return;

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(`data:${audioMimeType};base64,${audioBase64}`);
    audioRef.current = audio;
    
    // Track audio loading and duration
    audio.onloadedmetadata = () => {
      setAudioDuration(audio.duration);
    };

    // Track current time for progress bar and image switching
    audio.ontimeupdate = () => {
      const currentTime = audio.currentTime;
      const duration = audio.duration || 1;
      
      // Update progress bar and current time for subtitle
      setAudioProgress((currentTime / duration) * 100);
      setCurrentAudioTime(currentTime);
      
      // Calculate which image should be shown
      if (images.length > 0) {
        const timePerImage = getTimePerImage(duration, images.length);
        setTimePerImageState(timePerImage);
        const newImageIndex = Math.min(
          Math.floor(currentTime / timePerImage),
          images.length - 1
        );
        setCurrentImageIndex(newImageIndex);
      }
    };

    audio.onended = () => {
      setIsPlaying(false);
      setAudioProgress(0);
      setCurrentAudioTime(0);
      setCurrentImageIndex(0);
      setPrevImageIndex(-1);
      setImageTransitioning(false);
    };

    audio.play();
    setIsPlaying(true);
  };

  // Export video as WebM
  const handleExport = async () => {
    if (!audioBase64 || images.length === 0) {
      setError("Cần có audio và hình ảnh để xuất video");
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      // Get canvas dimensions based on aspect ratio
      const getDimensions = () => {
        switch (aspectRatio) {
          case "9:16": return { width: 720, height: 1280 };
          case "1:1": return { width: 720, height: 720 };
          case "16:9":
          default: return { width: 1280, height: 720 };
        }
      };

      const { width, height } = getDimensions();
      
      // Create canvas
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Load all images first
      const loadedImages = await Promise.all(
        images.map((src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
          });
        })
      );

      // Create audio element to get duration
      const audioElement = new Audio(`data:${audioMimeType};base64,${audioBase64}`);
      await new Promise((resolve) => {
        audioElement.onloadedmetadata = resolve;
        audioElement.onerror = () => resolve(); // Continue even if audio fails
      });

      const totalDuration = audioElement.duration || 10;
      const timePerImage = totalDuration / images.length;
      const fps = 30;
      const totalFrames = Math.ceil(totalDuration * fps);

      // Create MediaRecorder
      const stream = canvas.captureStream(fps);
      
      // Create audio context and add audio track
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Decode audio
      const audioResponse = await fetch(`data:${audioMimeType};base64,${audioBase64}`);
      const audioArrayBuffer = await audioResponse.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(audioArrayBuffer);
      
      // Create audio source and destination (only for recording, not playback)
      const audioDestination = audioContext.createMediaStreamDestination();
      const audioSource = audioContext.createBufferSource();
      audioSource.buffer = audioBuffer;
      audioSource.connect(audioDestination);
      // Note: Do NOT connect to audioContext.destination to avoid auto-playing audio
      
      // Add audio track to stream
      stream.addTrack(audioDestination.stream.getAudioTracks()[0]);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9,opus",
        videoBitsPerSecond: 5000000,
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      // Split script into words for subtitle display
      const words = script.trim().split(/\s+/).filter(Boolean);
      const wordsPerSecond = words.length / totalDuration;
      
      // Apply effect to image and render subtitle
      const applyEffect = (img, progress, imageProgress, currentTime) => {
        ctx.save();
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, width, height);

        // Calculate image dimensions to cover canvas
        const imgAspect = img.width / img.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight;
        if (imgAspect > canvasAspect) {
          drawHeight = height * 1.2; // Extra for effects
          drawWidth = drawHeight * imgAspect;
        } else {
          drawWidth = width * 1.2;
          drawHeight = drawWidth / imgAspect;
        }

        let offsetX = (width - drawWidth) / 2;
        let offsetY = (height - drawHeight) / 2;

        // Apply visual effects
        switch (effect) {
          case "zoom-in":
            const zoomIn = 1 + imageProgress * 0.15;
            drawWidth *= zoomIn;
            drawHeight *= zoomIn;
            offsetX = (width - drawWidth) / 2;
            offsetY = (height - drawHeight) / 2;
            break;
          case "zoom-out":
            const zoomOut = 1.15 - imageProgress * 0.15;
            drawWidth *= zoomOut;
            drawHeight *= zoomOut;
            offsetX = (width - drawWidth) / 2;
            offsetY = (height - drawHeight) / 2;
            break;
          case "pan-left":
            offsetX = (width - drawWidth) * (1 - imageProgress);
            break;
          case "pan-right":
            offsetX = (width - drawWidth) * imageProgress;
            break;
          case "static":
          default:
            // Center the image
            offsetX = (width - drawWidth) / 2;
            offsetY = (height - drawHeight) / 2;
            break;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Render subtitle if enabled
        if (subtitleEnabled && words.length > 0) {
          const fontSize = Math.round(width * 0.045); // Responsive font size
          const lineHeight = fontSize * 1.4;
          const maxWidth = width * 0.85;
          const bottomPadding = height * 0.08;
          
          // Calculate current word index based on time
          const currentWordIndex = Math.floor(currentTime * wordsPerSecond);
          
          // Get a window of words to display (show ~6-8 words at a time)
          const wordsPerChunk = 6;
          const chunkIndex = Math.floor(currentWordIndex / wordsPerChunk);
          const startWord = chunkIndex * wordsPerChunk;
          const endWord = Math.min(startWord + wordsPerChunk, words.length);
          const displayWords = words.slice(startWord, endWord);
          const localWordIndex = currentWordIndex - startWord;
          
          if (displayWords.length > 0) {
            ctx.font = `bold ${fontSize}px "SF Pro Display", "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            
            // Measure text for background
            const fullText = displayWords.join(" ");
            const textMetrics = ctx.measureText(fullText);
            const textWidth = Math.min(textMetrics.width, maxWidth);
            const bgPadding = fontSize * 0.5;
            const bgHeight = lineHeight + bgPadding * 2;
            const bgY = height - bottomPadding - bgHeight;
            
            // Draw semi-transparent background
            ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
            const bgRadius = fontSize * 0.3;
            const bgX = (width - textWidth) / 2 - bgPadding;
            ctx.beginPath();
            ctx.roundRect(bgX, bgY, textWidth + bgPadding * 2, bgHeight, bgRadius);
            ctx.fill();
            
            // Calculate text Y position
            const textY = height - bottomPadding - bgPadding;
            
            // Apply subtitle effects
            switch (subtitleEffect) {
              case "highlight":
                // Highlight current word
                let xPos = (width - textWidth) / 2;
                displayWords.forEach((word, idx) => {
                  const wordWidth = ctx.measureText(word + " ").width;
                  if (idx < localWordIndex) {
                    ctx.fillStyle = "#ffffff";
                  } else if (idx === localWordIndex) {
                    ctx.fillStyle = "#facc15"; // Yellow highlight
                  } else {
                    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
                  }
                  ctx.textAlign = "left";
                  ctx.fillText(word, xPos, textY);
                  xPos += wordWidth;
                });
                break;
                
              case "bounce":
                // Bounce effect on current word
                let bxPos = (width - textWidth) / 2;
                displayWords.forEach((word, idx) => {
                  const wordWidth = ctx.measureText(word + " ").width;
                  let yOffset = 0;
                  if (idx === localWordIndex) {
                    // Bounce animation
                    const bouncePhase = (currentTime * 8) % 1;
                    yOffset = Math.sin(bouncePhase * Math.PI) * fontSize * 0.2;
                    ctx.fillStyle = "#facc15";
                  } else {
                    ctx.fillStyle = "#ffffff";
                  }
                  ctx.textAlign = "left";
                  ctx.fillText(word, bxPos, textY - yOffset);
                  bxPos += wordWidth;
                });
                break;
                
              case "typewriter":
                // Typewriter effect - reveal letters
                const chunkStartTime = chunkIndex * (wordsPerChunk / wordsPerSecond);
                const timeInChunk = currentTime - chunkStartTime;
                const charsPerSecond = 15;
                const visibleChars = Math.floor(timeInChunk * charsPerSecond);
                
                let typeText = displayWords.join(" ");
                let visibleText = typeText.substring(0, visibleChars);
                
                ctx.fillStyle = "#ffffff";
                ctx.textAlign = "center";
                ctx.fillText(visibleText, width / 2, textY);
                break;
                
              case "karaoke":
                // Karaoke effect - gradual color fill
                ctx.textAlign = "center";
                
                // Draw shadow/background text
                ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
                ctx.fillText(fullText, width / 2, textY);
                
                // Calculate progress through current chunk
                const karaokeProgress = (localWordIndex + 0.5) / displayWords.length;
                
                // Create gradient for karaoke effect
                const gradientWidth = textWidth * 1.2;
                const gradientX = (width - gradientWidth) / 2;
                const gradient = ctx.createLinearGradient(gradientX, 0, gradientX + gradientWidth, 0);
                gradient.addColorStop(0, "#facc15");
                gradient.addColorStop(Math.max(0, karaokeProgress - 0.1), "#facc15");
                gradient.addColorStop(Math.min(1, karaokeProgress + 0.1), "rgba(255, 255, 255, 0.3)");
                gradient.addColorStop(1, "rgba(255, 255, 255, 0.3)");
                
                ctx.fillStyle = gradient;
                ctx.fillText(fullText, width / 2, textY);
                break;
                
              default:
                // Default: simple white text
                ctx.fillStyle = "#ffffff";
                ctx.textAlign = "center";
                ctx.fillText(fullText, width / 2, textY);
            }
          }
        }
        
        ctx.restore();
      };

      // Render frames
      let frameCount = 0;
      const framePromise = new Promise((resolve) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: "video/webm" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `video_${Date.now()}.webm`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          resolve();
        };
      });

      mediaRecorder.start();
      audioSource.start();

      // Render loop
      const renderFrame = () => {
        if (frameCount >= totalFrames) {
          mediaRecorder.stop();
          audioContext.close();
          return;
        }

        const currentTime = frameCount / fps;
        const imageIndex = Math.min(
          Math.floor(currentTime / timePerImage),
          loadedImages.length - 1
        );
        
        // Progress within current image (0-1)
        const imageStartTime = imageIndex * timePerImage;
        const imageProgress = Math.min(
          (currentTime - imageStartTime) / timePerImage,
          1
        );

        applyEffect(loadedImages[imageIndex], currentTime / totalDuration, imageProgress, currentTime);
        
        frameCount++;
        
        // Use requestAnimationFrame for smooth rendering
        if (frameCount < totalFrames) {
          setTimeout(renderFrame, 1000 / fps);
        } else {
          setTimeout(() => {
            mediaRecorder.stop();
            audioContext.close();
          }, 100);
        }
      };

      renderFrame();
      await framePromise;

    } catch (err) {
      console.error("Export error:", err);
      setError(sanitizeError(err, "Lỗi xuất video"));
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate word count
  const wordCount = script.trim().split(/\s+/).filter(Boolean).length;

  // Render step header
  const renderStepHeader = (stepNum, title, status) => {
    const isActive = activeStep === stepNum;
    const isCompleted = completedSteps.includes(stepNum);
    const isLocked = stepNum > 1 && !completedSteps.includes(stepNum - 1) && !isActive;

    return (
      <div
        className="step-header"
        onClick={() => !isLocked && setActiveStep(stepNum)}
      >
        <div className="step-number">
          {isCompleted ? <FiCheck /> : stepNum}
        </div>
        <div className="step-title">{title}</div>
        <div className="step-status">{status}</div>
        <FiChevronDown
          style={{
            transform: isActive ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.2s",
            color: "#6b7280",
          }}
        />
      </div>
    );
  };

  return (
    <div className="advanced-mode-panel">
      {/* Error message */}
      {error && (
        <div className="error-message">
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}>
            <FiX />
          </button>
        </div>
      )}

      {/* Step 1: Upload Images */}
      <div className={`step-accordion ${activeStep === 1 ? "active" : ""} ${completedSteps.includes(1) ? "completed" : ""}`}>
        {renderStepHeader(1, "Hình ảnh của bạn", images.length > 0 ? `${images.length} ảnh` : "")}
        
        <div className="step-content">
          {/* Image uploader */}
          {images.length === 0 ? (
            <div
              className="image-uploader"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="image-uploader-icon">📸</div>
              <div className="image-uploader-text">Kéo thả hoặc click để tải ảnh</div>
              <div className="image-uploader-hint">Tối đa 10 ảnh</div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={imageItems.map((item) => item.id)}
                strategy={rectSortingStrategy}
              >
                <div className="image-preview-grid">
                  {imageItems.map((item, index) => (
                    <SortableImageItem
                      key={item.id}
                      id={item.id}
                      src={item.src}
                      index={index}
                      onRemove={removeImage}
                    />
                  ))}
                  {imageItems.length < 10 && (
                    <div
                      className="image-uploader"
                      style={{ aspectRatio: "1", padding: "1rem" }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FiUpload style={{ fontSize: "1.5rem", color: "#9ca3af" }} />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => handleFileSelect(e.target.files)}
                      />
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Duration selector */}
          <div style={{ marginTop: "1.5rem" }}>
            <label style={{ color: "#374151", marginBottom: "0.75rem", display: "block", fontWeight: "500" }}>
              Độ dài video mong muốn
            </label>
            <div className="duration-selector">
              {DURATION_PRESETS.map((preset) => (
                <div
                  key={preset.id}
                  className={`duration-option ${duration === preset.id ? "selected" : ""} ${preset.recommended ? "recommended" : ""}`}
                  onClick={() => setDuration(preset.id)}
                >
                  <div className="duration-time">{preset.label}</div>
                  <div className="duration-desc">{preset.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional info */}
          <div className="additional-info-section">
            <label className="additional-info-label">
              <FiInfo />
              Thông tin bổ sung (tùy chọn)
            </label>
            <textarea
              className="additional-info-textarea"
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder={"Ví dụ:\n• Tên sản phẩm: Kem chống nắng ABC SPF50\n• Giá: 299.000đ (giảm 30%)\n• Đặc điểm nổi bật: Không nhờn, thấm nhanh\n• Đối tượng: Phụ nữ 25-35 tuổi\n• Giọng điệu: Trẻ trung, năng động"}
              maxLength={1000}
            />
            <div className="additional-info-hint">
              Cung cấp thông tin để AI tạo kịch bản chính xác và cá nhân hóa hơn
              {additionalInfo.length > 0 && (
                <span> • {additionalInfo.length}/1000</span>
              )}
            </div>
          </div>

          {/* Generate script button */}
          <button
            className={`generate-button ${isGeneratingScript ? 'loading' : ''}`}
            onClick={handleGenerateScript}
            disabled={images.length === 0 || isGeneratingScript}
            style={{ marginTop: "1rem" }}
          >
            {isGeneratingScript ? (
              <>
                <span className="spinner"></span>
                <span>Đang tạo kịch bản...</span>
              </>
            ) : (
              <>
                <FiRefreshCw />
                <span>Tạo kịch bản AI</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Step 2: Script */}
      <div className={`step-accordion ${activeStep === 2 ? "active" : ""} ${completedSteps.includes(2) ? "completed" : ""}`}>
        {renderStepHeader(2, "Kịch bản AI", script ? `~${wordCount} từ` : "")}
        
        <div className="step-content">
          <div className="script-editor">
            <textarea
              className="script-textarea"
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="Kịch bản sẽ xuất hiện ở đây sau khi AI phân tích hình ảnh..."
            />
            <div className="script-stats">
              <span>{wordCount} từ</span>
              <span>•</span>
              <span>~{Math.round(wordCount / 2.5)} giây</span>
            </div>
          </div>

          <button
            className="generate-button secondary"
            onClick={handleGenerateScript}
            disabled={isGeneratingScript}
            style={{ marginBottom: "1rem" }}
          >
            <FiRefreshCw />
            <span>Tạo lại kịch bản</span>
          </button>

          <button
            className="generate-button"
            onClick={() => {
              setCompletedSteps((prev) => [...prev.filter((s) => s !== 2), 2]);
              setActiveStep(3);
            }}
            disabled={!script.trim()}
          >
            <span>Tiếp tục chọn giọng đọc</span>
          </button>
        </div>
      </div>

      {/* Step 3: Voice */}
      <div className={`step-accordion ${activeStep === 3 ? "active" : ""} ${completedSteps.includes(3) ? "completed" : ""}`}>
        {renderStepHeader(3, "Giọng đọc", selectedVoice)}
        
        <div className="step-content">
          {/* Voice selector */}
          <label style={{ color: "#374151", marginBottom: "0.75rem", display: "block", fontWeight: "500" }}>
            Chọn giọng đọc
          </label>
          <div className="voice-groups-container">
            {VOICE_GROUPS.map((group) => (
              <div key={group.label} className="voice-group">
                <div className="voice-group-label">{group.label}</div>
                <div className="voice-selector-grid">
                  {group.voices.map((voice) => (
                    <div
                      key={voice.id}
                      className={`voice-option ${selectedVoice === voice.id ? "selected" : ""}`}
                      onClick={() => setSelectedVoice(voice.id)}
                    >
                      <div className="voice-name">{voice.name}</div>
                      <div className="voice-category">{voice.category}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Speed selector */}
          <div style={{ marginTop: "1.5rem" }}>
            <label style={{ color: "#374151", marginBottom: "0.75rem", display: "block", fontWeight: "500" }}>
              Tốc độ đọc: <span style={{ color: "#2563eb", fontWeight: "bold" }}>{speed}x</span>
            </label>
            <div className="speed-button-group">
              {SPEED_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`speed-button ${speed === opt.value ? "selected" : ""}`}
                  onClick={() => setSpeed(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate audio button */}
          <button
            className={`generate-button ${isGeneratingAudio ? 'loading' : ''}`}
            onClick={handleGenerateAudio}
            disabled={isGeneratingAudio}
            style={{ marginTop: "1.5rem" }}
          >
            {isGeneratingAudio ? (
              <>
                <span className="spinner"></span>
                <span>Đang tạo giọng đọc...</span>
              </>
            ) : (
              <>
                🎤 <span>Tạo giọng đọc</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Step 4: Export */}
      <div className={`step-accordion ${activeStep === 4 ? "active" : ""} ${completedSteps.includes(4) ? "completed" : ""}`}>
        {renderStepHeader(4, "Hiệu ứng & Xuất video", audioBase64 ? "Sẵn sàng" : "")}
        
        <div className="step-content">
          {/* Aspect ratio */}
          <label style={{ color: "#374151", marginBottom: "0.75rem", display: "block", fontWeight: "500" }}>
            Tỷ lệ khung hình
          </label>
          <div className="aspect-ratio-selector">
            {ASPECT_RATIOS.map((ratio) => (
              <div
                key={ratio.id}
                className={`aspect-ratio-option ${aspectRatio === ratio.id ? "selected" : ""}`}
                onClick={() => setAspectRatio(ratio.id)}
              >
                <div className="aspect-ratio-icon">{ratio.icon}</div>
                <div className="aspect-ratio-label">{ratio.label}</div>
                <div className="aspect-ratio-desc">{ratio.desc}</div>
              </div>
            ))}
          </div>

          {/* Visual effect */}
          <label style={{ color: "#374151", marginBottom: "0.75rem", display: "block", marginTop: "1.5rem", fontWeight: "500" }}>
            Hiệu ứng hình ảnh
          </label>
          <div className="effect-selector">
            {VISUAL_EFFECTS.map((eff) => (
              <div
                key={eff.id}
                className={`effect-option ${effect === eff.id ? "selected" : ""}`}
                onClick={() => setEffect(eff.id)}
              >
                <span>{eff.icon}</span>
                <span>{eff.label}</span>
              </div>
            ))}
          </div>

          {/* Subtitle settings */}
          <div className="subtitle-settings" style={{ marginTop: "1.5rem" }}>
            <div className="subtitle-toggle">
              <label>Phụ đề tự động</label>
              <div
                className={`toggle-switch ${subtitleEnabled ? "active" : ""}`}
                onClick={() => setSubtitleEnabled(!subtitleEnabled)}
              />
            </div>
            
            {subtitleEnabled && (
              <div className="effect-selector">
                {SUBTITLE_EFFECTS.map((eff) => (
                  <div
                    key={eff.id}
                    className={`effect-option ${subtitleEffect === eff.id ? "selected" : ""}`}
                    onClick={() => setSubtitleEffect(eff.id)}
                  >
                    <span>{eff.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview & Play */}
          {audioBase64 && (
            <div className="video-preview-container" style={{ marginTop: "1.5rem" }}>
              <div
                className="video-preview-canvas"
                style={{
                  ...(aspectRatio === "9:16"
                    ? { width: "315px", height: "560px", maxWidth: "100%" }
                    : aspectRatio === "1:1"
                    ? { width: "400px", height: "400px", maxWidth: "100%" }
                    : { width: "100%", aspectRatio: "16/9" }),
                  margin: "0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* Current image */}
                {images[currentImageIndex] && (
                  <img
                    key={`current-${currentImageIndex}`}
                    src={images[currentImageIndex]}
                    alt={`Preview ${currentImageIndex + 1}`}
                    className={`preview-image ${isPlaying ? `effect-${effect}` : ""}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      "--effect-duration": `${timePerImageState}s`,
                    }}
                  />
                )}
                {/* Subtitle overlay */}
                {subtitleEnabled && isPlaying && script.trim() && audioDuration > 0 && (() => {
                  const words = script.trim().split(/\s+/).filter(Boolean);
                  if (words.length === 0) return null;
                  const wordsPerSecond = words.length / audioDuration;
                  const currentWordIndex = Math.floor(currentAudioTime * wordsPerSecond);
                  const wordsPerChunk = 6;
                  const chunkIndex = Math.floor(currentWordIndex / wordsPerChunk);
                  const startWord = chunkIndex * wordsPerChunk;
                  const endWord = Math.min(startWord + wordsPerChunk, words.length);
                  const displayWords = words.slice(startWord, endWord);
                  const localWordIndex = currentWordIndex - startWord;
                  if (displayWords.length === 0) return null;
                  
                  return (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "32px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 10,
                        background: "rgba(0, 0, 0, 0.7)",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        maxWidth: "85%",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {displayWords.map((word, idx) => {
                        let color = "#ffffff";
                        let fontWeight = "normal";
                        let transform = "none";
                        let display = "inline-block";
                        
                        switch (subtitleEffect) {
                          case "highlight":
                            if (idx < localWordIndex) {
                              color = "#ffffff";
                            } else if (idx === localWordIndex) {
                              color = "#facc15";
                              fontWeight = "bold";
                            } else {
                              color = "rgba(255, 255, 255, 0.5)";
                            }
                            break;
                          case "bounce":
                            if (idx === localWordIndex) {
                              color = "#facc15";
                              fontWeight = "bold";
                              const bouncePhase = (currentAudioTime * 8) % 1;
                              const yOffset = Math.sin(bouncePhase * Math.PI) * 4;
                              transform = `translateY(${-yOffset}px)`;
                            }
                            break;
                          case "typewriter":
                            // Show words progressively
                            if (idx > localWordIndex) {
                              color = "transparent";
                            }
                            break;
                          case "karaoke":
                            if (idx < localWordIndex) {
                              color = "#facc15";
                            } else if (idx === localWordIndex) {
                              color = "#facc15";
                              fontWeight = "bold";
                            } else {
                              color = "rgba(255, 255, 255, 0.3)";
                            }
                            break;
                          default:
                            break;
                        }
                        
                        return (
                          <span
                            key={idx}
                            style={{
                              color,
                              fontWeight,
                              display,
                              transform,
                              transition: "color 0.15s, transform 0.1s",
                              fontSize: "14px",
                              marginRight: idx < displayWords.length - 1 ? "5px" : "0",
                              fontFamily: '"SF Pro Display", "Segoe UI", Arial, sans-serif',
                            }}
                          >
                            {word}
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}
                {/* Image counter */}
                {images.length > 1 && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "8px",
                      right: "8px",
                      background: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      zIndex: 11,
                    }}
                  >
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>
              <div className="video-preview-controls">
                <button className="play-button" onClick={handlePlayAudio}>
                  {isPlaying ? "⏸" : "▶"}
                </button>
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ 
                      width: `${audioProgress}%`,
                      transition: "width 0.1s linear"
                    }} 
                  />
                </div>
                {/* Time display */}
                {audioDuration > 0 && (
                  <div style={{ color: "#6b7280", fontSize: "12px", marginLeft: "8px", minWidth: "80px" }}>
                    {Math.floor((audioProgress / 100) * audioDuration)}s / {Math.floor(audioDuration)}s
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Export button */}
          <div className="export-buttons" style={{ marginTop: "1.5rem" }}>
            <button
              className={`export-button primary ${isExporting ? 'loading' : ''}`}
              onClick={handleExport}
              disabled={!audioBase64 || isExporting}
              style={{ width: "100%", minHeight: "48px" }}
            >
              {isExporting ? (
                <>
                  <span className="spinner"></span>
                  <span>Đang xuất video...</span>
                </>
              ) : (
                <>
                  <FiDownload />
                  <span>Tải video</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedModePanel;
